import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { t, statusLabel } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import Bracket from "../components/Bracket";
import TournamentChat from "./tournament/TournamentChat";
import RefereePanel from "./tournament/RefereePanel";
import BeyPongPlayerLockCard from "./tournament/BeyPongPlayerLockCard";
import { ArrowLeft, Trophy, MapPin, Calendar, CurrencyDollar, Users, SignIn, X, NavigationArrow } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL ? BACKEND_URL.replace(/^http/, "ws") : "";

export default function TournamentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("bracket");
  const [myDecks, setMyDecks] = useState([]);
  const [myDeckSets, setMyDeckSets] = useState([]);
  const [joinOpen, setJoinOpen] = useState(false);
  const [pickedDeck, setPickedDeck] = useState("");
  const [pickedSet, setPickedSet] = useState("");
  const [busy, setBusy] = useState(false);
  const [liveOn, setLiveOn] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);

  const reload = () => api.get(`/tournaments/${id}`).then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    reload();
  }, [id]);

  useEffect(() => {
    if (user && user.role === "player") {
      api.get("/me/decks").then((r) => setMyDecks(r.data)).catch(() => {});
      api.get("/me/decksets").then((r) => setMyDeckSets(r.data)).catch(() => {});
    }
  }, [user]);

  // Live WebSocket subscription for bracket/roster updates
  useEffect(() => {
    if (!id || !WS_URL) return;
    let closed = false;
    let retry = null;
    const open = () => {
      if (closed) return;
      try {
        const ws = new WebSocket(`${WS_URL}/api/ws/tournaments/${id}`);
        wsRef.current = ws;
        ws.onopen = () => setLiveOn(true);
        ws.onclose = () => {
          setLiveOn(false);
          if (!closed) retry = setTimeout(open, 3000);
        };
        ws.onerror = () => {
          try { ws.close(); } catch (e) { /* noop */ }
        };
        ws.onmessage = (msg) => {
          try {
            const data = JSON.parse(msg.data);
            if (data.event === "connected") return;
            setLastEvent(data.event);
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("beyx-tournament-event", { detail: data }));
            }
            reload();
          } catch (e) { /* noop */ }
        };
      } catch (e) {
        if (!closed) retry = setTimeout(open, 3000);
      }
    };
    open();
    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      try { wsRef.current && wsRef.current.close(); } catch (e) { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!data)
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-20 font-mono text-white/40">
        {t.loading}
      </div>
    );

  const { tournament: tt, matches, players } = data;
  const isPlayer = user && user.role === "player";
  const myPid = user?.player_id;
  const alreadyJoined = myPid && (tt.player_ids || []).includes(myPid);
  const canJoin =
    isPlayer && !alreadyJoined &&
    tt.status === "upcoming" &&
    (tt.player_ids || []).length < tt.max_players;
  const isFull = (tt.player_ids || []).length >= tt.max_players;

  const isAdmin = user?.role === "admin";
  const isHost = user && players.find((p) => p.id === myPid)?.handle === tt.host_handle;
  // Iter 33 — merge and dedupe the list + legacy singular field.
  const modIds = [...new Set([
    ...(tt.moderator_player_ids || []),
    ...(tt.moderator_player_id ? [tt.moderator_player_id] : []),
  ])];
  const isMod = myPid && modIds.includes(myPid);
  const isReferee = myPid && (tt.referee_player_ids || []).includes(myPid);
  const canRef = !!(isAdmin || isHost || isMod || isReferee);

  const join = async () => {
    setBusy(true);
    try {
      await api.post(`/tournaments/${id}/join`, {
        deck_id: pickedDeck || null,
        deck_set_id: pickedSet || null,
      });
      toast.success(t.joined);
      setJoinOpen(false);
      setPickedDeck("");
      setPickedSet("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t.failed);
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    if (!window.confirm(t.leaveTournament + "?")) return;
    setBusy(true);
    try {
      await api.delete(`/tournaments/${id}/join`);
      toast.success(t.left);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t.failed);
    } finally {
      setBusy(false);
    }
  };
  const knockoutMatches = matches.filter((m) => (m.stage || "knockout") === "knockout");
  const groupMatches = matches.filter((m) => m.stage === "group");
  const finalMatch = knockoutMatches.length ? knockoutMatches[knockoutMatches.length - 1] : null;
  const champion =
    finalMatch && finalMatch.winner_id
      ? players.find((p) => p.id === finalMatch.winner_id)
      : null;

  const isGroupKnockout = tt.format === "group_knockout";

  return (
    <div>
      <section className="relative border-b border-white/10">
        <div
          className="aspect-[21/9] bg-cover bg-center"
          style={{ backgroundImage: `url('${tt.banner_url}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/80 to-[#05050A]/30" />
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-8">
            <Link
              to="/tournaments"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-white/60 hover:text-white"
              data-testid="back-link"
            >
              <ArrowLeft size={12} /> {t.navTournaments}
            </Link>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <span
                className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 ${
                  tt.status === "live"
                    ? "bg-[#FF0055] text-white"
                    : tt.status === "upcoming"
                    ? "border border-[#00E5FF] text-[#00E5FF]"
                    : "border border-white/30 text-white/60"
                }`}
              >
                {statusLabel(tt.status)}
              </span>
              <span className="text-xs uppercase tracking-[0.2em] font-mono text-white/60">
                {tt.format === "group_knockout" ? t.formatGroupKnockout : t.formatSingleElim}
              </span>
              {tt.created_by === "community" && (
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 bg-[#E2FF00]/15 text-[#E2FF00] border border-[#E2FF00]/40">
                  {t.community} · {tt.host_handle}
                </span>
              )}
              {tt.approval_status === "pending" && (
                <span
                  data-testid="approval-pending-badge"
                  className="text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-1 bg-[#E2FF00]/10 text-[#E2FF00] border border-[#E2FF00]/40"
                >
                  ⏳ {t.approvalPending}
                </span>
              )}
              {modIds.length > 0 && (
                <span
                  data-testid="moderator-badge"
                  className="text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-1 bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/40"
                >
                  {t.moderatorBadge}
                  {modIds.length === 1 ? " · " : ` × ${modIds.length} · `}
                  {modIds
                    .map((mid) => (players.find((p) => p.id === mid) || {}).handle)
                    .filter(Boolean)
                    .join(", ") || "—"}
                </span>
              )}
              {(tt.referee_player_ids || []).length > 0 && (
                <span
                  data-testid="referees-badge"
                  className="text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-1 bg-[#9B7AFF]/10 text-[#9B7AFF] border border-[#9B7AFF]/40"
                >
                  {t.refereeBadge} × {(tt.referee_player_ids || []).length}
                </span>
              )}
              {tt.included_in_leaderboard && (
                <span
                  data-testid="ranked-badge"
                  className="text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-1 bg-[#E2FF00]/10 text-[#E2FF00] border border-[#E2FF00]/40"
                >
                  ★ {t.rankedBadge}
                </span>
              )}
              {tt.format === "pokeblade" && (
                <span
                  data-testid="pokeblade-badge"
                  className="text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-1 bg-[#9B7AFF]/10 text-[#9B7AFF] border border-[#9B7AFF]/40"
                >
                  POKEBLADE · 6 BEYS
                </span>
              )}
              <span
                data-testid="live-indicator"
                className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-1 border ${
                  liveOn
                    ? "bg-[#FF0055]/10 text-[#FF0055] border-[#FF0055]/40"
                    : "text-white/30 border-white/15"
                }`}
                title={lastEvent ? `last: ${lastEvent}` : ""}
              >
                <span className="relative inline-flex w-2 h-2">
                  {liveOn && (
                    <span className="absolute inline-flex w-full h-full rounded-full bg-[#FF0055] opacity-60 animate-ping" />
                  )}
                  <span
                    className={`relative inline-flex w-2 h-2 rounded-full ${
                      liveOn ? "bg-[#FF0055]" : "bg-white/30"
                    }`}
                  />
                </span>
                {liveOn ? t.liveOn : t.liveOff}
              </span>
            </div>
            <h1
              className="font-display font-black uppercase italic text-5xl sm:text-7xl mt-3 leading-none"
              data-testid="tournament-name"
            >
              {tt.name}
            </h1>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        {tt.approval_status === "pending" && (
          <div
            data-testid="approval-banner"
            className="mb-6 border border-[#E2FF00]/40 bg-[#E2FF00]/5 px-5 py-4 text-sm text-[#E2FF00] font-mono flex items-center gap-3"
          >
            <span className="text-xl">⏳</span>
            <span>{t.awaitingApproval}</span>
          </div>
        )}
        <div className="grid md:grid-cols-4 gap-4 mb-10">
          <Info icon={MapPin} label={t.location} value={tt.location} />
          <Info icon={Calendar} label={t.start} value={new Date(tt.start_date).toLocaleDateString("pl-PL")} />
          <Info icon={CurrencyDollar} label={t.prizePool} value={tt.prize_pool} accent />
          {/* Iter 33 — count roster only. The backend's `players` array
              also includes moderators/referees (their profile blobs), so
              using .length here would over-count seats. */}
          <Info icon={Users} label={t.bladers} value={`${(tt.player_ids || []).length}/${tt.max_players}`} />
        </div>
        {tt.location && (
          <div className="-mt-6 mb-10 flex flex-wrap items-center gap-2">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(tt.location)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="maps-directions"
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 text-[10px] uppercase tracking-[0.25em] font-bold"
            >
              <NavigationArrow size={12} weight="fill" /> {t.getDirections}
            </a>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(tt.location)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="maps-open"
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 text-white/70 hover:text-white hover:border-white/30 text-[10px] uppercase tracking-[0.25em] font-bold"
            >
              <MapPin size={12} weight="fill" /> {t.openInMaps}
            </a>
          </div>
        )}

        {champion && (
          <div className="mb-10 border border-[#E2FF00]/30 bg-[#E2FF00]/5 p-5 flex items-center gap-4">
            <Trophy size={32} weight="fill" className="text-[#E2FF00]" />
            <div>
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E2FF00] font-mono">{t.champion}</div>
              <div className="font-display font-black uppercase italic text-3xl mt-1">{champion.handle}</div>
            </div>
          </div>
        )}

        {tt.description && <p className="text-white/70 max-w-2xl mb-6">{tt.description}</p>}

        {Array.isArray(tt.prize_breakdown) && tt.prize_breakdown.length > 0 && (
          <div className="mb-10 border border-[#E2FF00]/20 bg-[#E2FF00]/5 p-5" data-testid="prize-breakdown">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E2FF00] font-mono mb-3">
              // {t.prizeBreakdown}
            </div>
            <div className="space-y-1.5">
              {tt.prize_breakdown.map((p, i) => (
                <div key={i} className="flex items-baseline gap-3 text-sm">
                  <span className="font-display font-black italic text-2xl text-[#E2FF00] w-12">
                    #{p.place}
                  </span>
                  <span className="text-white">{p.item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Join / Leave / Check-in actions */}
        {tt.status === "upcoming" && (
          <div className="mb-8 flex items-center gap-3 flex-wrap">
            {!user || user === false ? (
              <Link
                to="/login"
                data-testid="login-to-join"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 font-bold uppercase tracking-[0.2em] text-xs"
              >
                <SignIn size={14} weight="fill" /> {t.navLogin} → {t.joinTournament}
              </Link>
            ) : alreadyJoined ? (
              <>
                <span className="px-4 py-2 bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/40 font-bold uppercase tracking-[0.2em] text-xs">
                  ✓ {t.alreadyJoined}
                </span>
                {tt.check_in_required && (
                  <CheckInBtn
                    tournament={tt}
                    user={user}
                    onCheckedIn={reload}
                  />
                )}
                <button
                  onClick={leave}
                  disabled={busy}
                  data-testid="leave-tournament-btn"
                  className="px-4 py-2 border border-white/20 text-white/60 hover:text-[#FF0055] hover:border-[#FF0055]/40 font-bold uppercase tracking-[0.2em] text-xs"
                >
                  <X size={12} className="inline mr-1" /> {t.leaveTournament}
                </button>
              </>
            ) : canJoin ? (
              <button
                onClick={() => setJoinOpen(true)}
                data-testid="open-join-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.2em] text-xs glow-pink"
              >
                <Trophy size={14} weight="fill" /> {t.joinTournament}
              </button>
            ) : isFull ? (
              <span className="px-4 py-2 border border-white/20 text-white/40 font-bold uppercase tracking-[0.2em] text-xs">
                {t.tournamentFull}
              </span>
            ) : null}
          </div>
        )}

        {joinOpen && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setJoinOpen(false)}>
            <div className="bg-[#0C0C14] border border-white/15 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-display font-black uppercase italic text-2xl">{t.joinTournament}</h3>
              <div className="mt-4 space-y-3">
                {myDeckSets.length > 0 && (
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-2">
                      {t.selectDeckSet}
                    </label>
                    <select
                      value={pickedSet}
                      onChange={(e) => { setPickedSet(e.target.value); if (e.target.value) setPickedDeck(""); }}
                      data-testid="join-set-select"
                      className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
                    >
                      <option value="" className="bg-[#0C0C14]">— {t.none} —</option>
                      {myDeckSets.map((s) => (
                        <option key={s.id} value={s.id} className="bg-[#0C0C14]">
                          {s.name} ({s.deck_ids.length} {t.tabBlades.toLowerCase()})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-2">
                    {t.selectDeck}
                  </label>
                  {myDecks.length === 0 ? (
                    <Link
                      to="/me"
                      className="block border border-[#00E5FF]/40 text-[#00E5FF] px-4 py-3 text-sm hover:bg-[#00E5FF]/5"
                      data-testid="create-deck-prompt"
                    >
                      {t.createFirstDeck} →
                    </Link>
                  ) : (
                    <select
                      value={pickedDeck}
                      onChange={(e) => { setPickedDeck(e.target.value); if (e.target.value) setPickedSet(""); }}
                      data-testid="join-deck-select"
                      className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
                    >
                      <option value="" className="bg-[#0C0C14]">— {t.none} —</option>
                      {myDecks.map((d) => (
                        <option key={d.id} value={d.id} className="bg-[#0C0C14]">
                          {d.name} ({d.archetype})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => setJoinOpen(false)} className="px-4 py-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60">{t.cancel}</button>
                <button
                  onClick={join}
                  disabled={busy}
                  data-testid="confirm-join-btn"
                  className="px-5 py-2 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs disabled:opacity-50"
                >
                  {busy ? t.creating : t.joinTournament}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="border-b border-white/10 flex gap-1 flex-wrap">
          {isGroupKnockout && (
            <TabBtn id="groups" tab={tab} setTab={setTab} label={t.groups} />
          )}
          <TabBtn id="bracket" tab={tab} setTab={setTab} label={t.bracket} />
          <TabBtn id="players" tab={tab} setTab={setTab} label={t.bladers} />
          <TabBtn id="chat" tab={tab} setTab={setTab} label={t.chat} />
          {canRef && <TabBtn id="ref" tab={tab} setTab={setTab} label={t.refereePanel} />}
        </div>

        <div className="mt-8">
          {/* Iter 40 — BEY-PONG: show a self-lock card for a player in a
              live BEY-PONG match so they can commit their selection +
              hidden order from their own device. */}
          {alreadyJoined && (
            <BeyPongPlayerLockCard
              tournament={tt}
              matches={matches}
              players={players}
              myPid={myPid}
              onLockedChange={reload}
            />
          )}
          {tab === "bracket" &&
            (knockoutMatches.length === 0 ? (
              <div className="text-center text-white/40 py-20 font-mono text-sm space-y-4">
                <div>{t.bracketEmpty}</div>
                {(isAdmin || isHost || isMod) && (
                  <div className="flex justify-center gap-2 flex-wrap">
                    <button
                      onClick={async () => {
                        try {
                          const r = await api.post(`/admin/tournaments/${tt.id}/seed-bracket`);
                          toast.success(`${t.bracketCreated}: ${r.data.count}`);
                          await reload();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || t.failed);
                        }
                      }}
                      data-testid="generate-bracket-btn"
                      className="px-5 py-2.5 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs"
                    >
                      ⚡ {t.generateBracket}
                    </button>
                    {isGroupKnockout && (tt.groups || []).length > 0 && (
                      <button
                        onClick={async () => {
                          try {
                            const r = await api.post(`/admin/tournaments/${tt.id}/generate-knockout`);
                            toast.success(`${t.knockoutGenerated}: ${r.data.count}`);
                            await reload();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || t.failed);
                          }
                        }}
                        data-testid="generate-knockout-btn"
                        className="px-5 py-2.5 border border-[#E2FF00]/40 text-[#E2FF00] hover:bg-[#E2FF00]/10 font-bold uppercase tracking-[0.25em] text-xs"
                      >
                        {t.generateKnockout}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : tt.format === "double_elim" ? (
              <div className="space-y-10">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#00E5FF] mb-3 font-mono">
                    // {t.bracketWinners}
                  </div>
                  <Bracket matches={knockoutMatches.filter((m) => m.bracket_type === "W")} players={players} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] mb-3 font-mono">
                    // {t.bracketLosers}
                  </div>
                  <Bracket matches={knockoutMatches.filter((m) => m.bracket_type === "L")} players={players} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E2FF00] mb-3 font-mono">
                    // {t.bracketGrandFinal}
                  </div>
                  <Bracket matches={knockoutMatches.filter((m) => m.bracket_type === "GF")} players={players} />
                </div>
              </div>
            ) : (
              <Bracket matches={knockoutMatches} players={players} />
            ))}
          {tab === "groups" && isGroupKnockout && (
            <Groups
              tournament={tt}
              groups={tt.groups || []}
              groupMatches={groupMatches}
              players={players}
            />
          )}
          {tab === "players" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {players.map((p) => {
                const ci = (tt.checked_in_player_ids || []).includes(p.id);
                return (
                  <Link
                    to={`/players/${p.id}`}
                    key={p.id}
                    data-testid={`player-tile-${p.id}`}
                    className="border border-white/10 bg-[#0C0C14] p-4 hover:border-[#00E5FF]/50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] font-mono text-white/40">{p.country}</div>
                      {ci && (
                        <span
                          data-testid={`checked-in-${p.id}`}
                          className="text-[9px] font-bold uppercase tracking-[0.25em] px-1.5 py-0.5 bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/40"
                        >
                          ✓ IN
                        </span>
                      )}
                    </div>
                    <div className="font-display font-bold uppercase italic text-xl mt-1">{p.handle}</div>
                    <div className="text-xs text-white/50 mt-2 font-mono">
                      {p.wins}W · {p.losses}L · {p.points} {t.pts}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
          {tab === "chat" && (
            <TournamentChat tournamentId={id} tt={tt} user={user} players={players} />
          )}
          {tab === "ref" && canRef && (
            <RefereePanel tournamentId={id} tt={tt} matches={data.matches} players={players} reload={reload} />
          )}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ id, tab, setTab, label }) {
  return (
    <button
      onClick={() => setTab(id)}
      data-testid={`tab-${id}`}
      className={`px-5 py-3 text-xs uppercase tracking-[0.25em] font-bold transition-colors border-b-2 -mb-px ${
        tab === id ? "text-[#00E5FF] border-[#00E5FF]" : "text-white/50 border-transparent hover:text-white"
      }`}
    >
      {label}
    </button>
  );
}

function Info({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-white/10 bg-[#0C0C14] p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">
        <Icon size={12} /> {label}
      </div>
      <div className={`mt-2 font-display font-bold italic text-xl ${accent ? "text-[#E2FF00]" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function Groups({ tournament, groups, groupMatches, players }) {
  const playerMap = Object.fromEntries(players.map((p) => [p.id, p]));
  const isBeyPong = tournament?.scoring_format === "bey_pong";
  const [bpStandings, setBpStandings] = useState(null);

  useEffect(() => {
    if (!isBeyPong || !tournament?.id) return;
    let cancelled = false;
    api.get(`/tournaments/${tournament.id}/beypong/standings`)
      .then((r) => { if (!cancelled) setBpStandings(r.data); })
      .catch(() => { if (!cancelled) setBpStandings({ standings: [], tiebreak_warning: false }); });
    return () => { cancelled = true; };
  }, [isBeyPong, tournament?.id, groupMatches.length]);

  if (isBeyPong) {
    return (
      <BeyPongGroups
        tournament={tournament}
        groups={groups}
        groupMatches={groupMatches}
        playerMap={playerMap}
        bpStandings={bpStandings}
      />
    );
  }

  // standings per group
  const standings = groups.map((g, gi) => {
    const tally = Object.fromEntries(g.map((pid) => [pid, { w: 0, l: 0, diff: 0 }]));
    for (const m of groupMatches) {
      if (m.group_index !== gi || m.status !== "completed") continue;
      const a = m.player_a_id, b = m.player_b_id;
      if (tally[a]) {
        tally[a].diff += m.score_a - m.score_b;
        tally[a][m.winner_id === a ? "w" : "l"] += 1;
      }
      if (tally[b]) {
        tally[b].diff += m.score_b - m.score_a;
        tally[b][m.winner_id === b ? "w" : "l"] += 1;
      }
    }
    const ranked = Object.entries(tally).sort((x, y) => y[1].w - x[1].w || y[1].diff - x[1].diff);
    return { gi, ranked };
  });

  return (
    <div className="space-y-8">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="groups-standings">
        {standings.map(({ gi, ranked }) => (
          <div key={gi} className="border border-white/10 bg-[#0C0C14]">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <span className="font-display font-black uppercase italic text-xl">
                {t.group} {String.fromCharCode(65 + gi)}
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-white/40">
                {ranked.length} {t.bladers}
              </span>
            </div>
            <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 border-b border-white/5">
              <div className="col-span-1">#</div>
              <div className="col-span-7">{t.blader}</div>
              <div className="col-span-1 text-right">W</div>
              <div className="col-span-1 text-right">L</div>
              <div className="col-span-2 text-right">+/-</div>
            </div>
            {ranked.map(([pid, s], i) => (
              <div key={pid} className="grid grid-cols-12 px-4 py-2 text-sm border-b last:border-b-0 border-white/5">
                <div className="col-span-1 font-mono text-white/40">{i + 1}</div>
                <div className="col-span-7 truncate">{playerMap[pid]?.handle || "—"}</div>
                <div className="col-span-1 text-right text-[#00E5FF] font-mono">{s.w}</div>
                <div className="col-span-1 text-right text-white/40 font-mono">{s.l}</div>
                <div className="col-span-2 text-right text-[#E2FF00] font-mono">{s.diff > 0 ? `+${s.diff}` : s.diff}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div>
        <h3 className="font-display font-black uppercase italic text-2xl mb-4">
          {t.matches}
        </h3>
        <div className="grid md:grid-cols-2 gap-2">
          {groupMatches.map((m) => {
            const a = playerMap[m.player_a_id], b = playerMap[m.player_b_id];
            const done = m.status === "completed";
            return (
              <div key={m.id} className="border border-white/10 bg-[#0C0C14] px-4 py-2 flex items-center gap-3 text-sm">
                <span className="text-[10px] font-mono text-white/30">
                  {String.fromCharCode(65 + (m.group_index ?? 0))}
                </span>
                <span className={`flex-1 truncate ${done && m.winner_id === a?.id ? "text-[#00E5FF] font-bold" : ""}`}>
                  {a?.handle || "—"}
                </span>
                <span className="font-mono text-white/60">{done ? `${m.score_a}-${m.score_b}` : "vs"}</span>
                <span className={`flex-1 truncate text-right ${done && m.winner_id === b?.id ? "text-[#00E5FF] font-bold" : ""}`}>
                  {b?.handle || "—"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// Iter 40 — BEY-PONG group standings + tiebreak nudge. Uses the
// /tournaments/{id}/beypong/standings endpoint as source of truth.
function BeyPongGroups({ tournament, groups, groupMatches, playerMap, bpStandings }) {
  const rows = bpStandings?.standings || [];
  const rowByPid = Object.fromEntries(rows.map((r) => [r.player_id, r]));
  const warning = !!bpStandings?.tiebreak_warning;

  const groupStandings = groups.map((g, gi) => {
    const inGroup = g
      .map((pid) => rowByPid[pid] || {
        player_id: pid, matches_won: 0, sets_won: 0, sets_lost: 0, points_scored: 0,
      })
      .sort((a, b) => (b.matches_won - a.matches_won) || (b.sets_won - a.sets_won) || (b.points_scored - a.points_scored));
    return { gi, rows: inGroup };
  });

  return (
    <div className="space-y-8">
      {warning && (
        <div
          data-testid="beypong-tiebreak-warning"
          className="border border-[#E2FF00]/40 bg-[#E2FF00]/10 px-4 py-3 text-xs font-mono text-[#E2FF00]"
        >
          ⓘ {t.beyPongTiebreakWarning}
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="beypong-groups-standings">
        {groupStandings.map(({ gi, rows: r }) => (
          <div key={gi} className="border border-white/10 bg-[#0C0C14]">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <span className="font-display font-black uppercase italic text-xl">
                {t.group} {String.fromCharCode(65 + gi)}
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-white/40">
                {r.length} {t.bladers}
              </span>
            </div>
            <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-white/40 border-b border-white/5">
              <div className="col-span-1">#</div>
              <div className="col-span-5">{t.blader}</div>
              <div className="col-span-2 text-right" title={t.beyPongColMW}>MW</div>
              <div className="col-span-2 text-right" title={t.beyPongColSW}>SW</div>
              <div className="col-span-2 text-right" title={t.beyPongColPts}>Pts</div>
            </div>
            {r.map((s, i) => (
              <div
                key={s.player_id}
                data-testid={`beypong-row-${gi}-${i}`}
                className="grid grid-cols-12 px-4 py-2 text-sm border-b last:border-b-0 border-white/5"
              >
                <div className="col-span-1 font-mono text-white/40">{i + 1}</div>
                <div className="col-span-5 truncate">{playerMap[s.player_id]?.handle || "—"}</div>
                <div className="col-span-2 text-right text-[#00E5FF] font-mono">{s.matches_won}</div>
                <div className="col-span-2 text-right text-white/70 font-mono">{s.sets_won}</div>
                <div className="col-span-2 text-right text-[#E2FF00] font-mono">{s.points_scored}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div>
        <h3 className="font-display font-black uppercase italic text-2xl mb-4">
          {t.matches}
        </h3>
        <div className="grid md:grid-cols-2 gap-2">
          {groupMatches.map((m) => {
            const a = playerMap[m.player_a_id], b = playerMap[m.player_b_id];
            const done = m.status === "completed";
            return (
              <div key={m.id} className="border border-white/10 bg-[#0C0C14] px-4 py-2 flex items-center gap-3 text-sm">
                <span className="text-[10px] font-mono text-white/30">
                  {String.fromCharCode(65 + (m.group_index ?? 0))}
                </span>
                <span className={`flex-1 truncate ${done && m.winner_id === a?.id ? "text-[#00E5FF] font-bold" : ""}`}>
                  {a?.handle || "—"}
                </span>
                <span className="font-mono text-white/60">{done ? `${m.score_a}-${m.score_b}` : "vs"}</span>
                <span className={`flex-1 truncate text-right ${done && m.winner_id === b?.id ? "text-[#00E5FF] font-bold" : ""}`}>
                  {b?.handle || "—"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function CheckInBtn({ tournament, user, onCheckedIn }) {
  const [busy, setBusy] = useState(false);
  const myPid = user?.player_id;
  const ci = (tournament.checked_in_player_ids || []).includes(myPid);

  // Compute window
  const start = new Date(tournament.start_date);
  const now = new Date();
  const open = new Date(start.getTime() - 24 * 3600 * 1000);
  const inWindow = now >= open && now <= start;

  const check = async () => {
    setBusy(true);
    try {
      await api.post(`/tournaments/${tournament.id}/check-in`);
      toast.success(t.checkedInToast);
      onCheckedIn();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failed);
    } finally { setBusy(false); }
  };

  if (ci) {
    return (
      <span
        data-testid="check-in-done"
        className="px-4 py-2 bg-[#E2FF00]/10 text-[#E2FF00] border border-[#E2FF00]/40 font-bold uppercase tracking-[0.2em] text-xs"
      >
        ✓ {t.checkedIn}
      </span>
    );
  }
  if (!inWindow) {
    const hrs = Math.max(0, Math.round((open - now) / 3600000));
    return (
      <span
        data-testid="check-in-window"
        className="px-4 py-2 border border-white/15 text-white/40 font-bold uppercase tracking-[0.2em] text-xs"
        title={`Opens ${open.toLocaleString()}`}
      >
        {hrs > 0 ? `${t.checkInOpens} ${hrs}h` : t.checkInClosed}
      </span>
    );
  }
  return (
    <button
      onClick={check}
      disabled={busy}
      data-testid="check-in-btn"
      className="px-4 py-2 bg-[#E2FF00]/15 hover:bg-[#E2FF00]/25 text-[#E2FF00] border border-[#E2FF00]/40 font-bold uppercase tracking-[0.2em] text-xs disabled:opacity-50"
    >
      ⏱ {t.checkInBtn}
    </button>
  );
}
